<script setup lang="ts">
import { onBeforeMount, onMounted, onBeforeUnmount, watch, computed, ref, watchEffect } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { useRoute } from 'vue-router'
import { isEmpty } from 'lodash-es'
import { type IMeta } from '@/utils/meta'
import { IDEVIEWMODELS, CONFIRM, AUTHMODELHS } from '@/utils/models'
import { useNavStore } from '@/stores/nav.store'
import settingService from '@/services/ide/settings/setting.service'
import ideService from '@/services/ide/ide.service'
import shareService from '@/services/ide/settings/share.service'
import projectsService, {
  type IProject,
  EXISTING_PROJECT_SESSION
} from '@/services/ide/projects.service'
import editorService from '@/services/ide/editor.service'
import blocklyService from '@/services/ide/languages/blockly/blockly.service'
import historyService from '@/services/ide/settings/history.service'
import jdroidService from '@/services/ide/jdroid.service'
import gtmService from '@/services/gtm.service'
import utilModelsService from '@/services/util.models.service'
import boardingService from '@/services/boarding.service'

import splitjsService, { ISplitDirection, type ISplitObj } from '@/services/ide/splitjs.service'
import liveCodingService from '@/services/ide/liveCoding.service'
import { useAuthStore } from '@/stores/auth.store'
import { useApiStore } from '@/stores/api.store'
import { useIdeStore } from '@/stores/ide.store'
import { useProjectManager } from '@/stores/projectManager.store'
import { useJdroidStore } from '@/stores/jdroid.store'
import { useUtilModelsStore } from '@/stores/util.models.store'
import { BoardingElement, currentReleaseDate } from '@/utils/ideBoardingSteps'
import { useAdvanceCompilerStore } from '@/stores/advanceCompiler.store'
import { useTabStore } from '@/stores/tabs.store'
import { useAdvanceCompilerDatabaseStore } from '@/stores/advanceCompilerDatabase.store'
import {
  LiveCodeActionsType,
  useLiveCodeStore,
  type ILiveCodeActions
} from '@/stores/liveCode.store'
import NotLoginModel from '@/components/ide/ide/settings/NotLoginModel.vue'
import Recaptcha from '@/components/shared/RecaptchaComp.vue'
import PrintBlocker from '@/components/ide/shared/PrintBlocker.vue'
import SettingsSideBar from '@/components/ide/ide/SettingsSideBar.vue'
import Ide from '@/components/ide/ide/IdeComp.vue'
import Tabs from '@/components/ide/ide/TabsComp.vue'
import FooterProduct from '@/components/footer/FooterProduct.vue'
import NewProject from '@/components/ide/ide/settings/ClearProjectModel.vue'
import MyProjectModel from '@/components/ide/ide/settings/MyProjectModel.vue'
import ExecurteHistoryModel from '@/components/ide/ide/settings/ExecurteHistoryModel.vue'
import SaveModel from '@/components/ide/ide/settings/SaveModel.vue'
import SaveAsModel from '@/components/ide/ide/settings/SaveAsModel.vue'
import EditableShareModel from '@/components/ide/ide/settings/EditableShareModel.vue'
import ProjectShareModal from '@/components/ide/ide/settings/ProjectShareModal.vue'
import IDESettingsModel from '@/components/ide/ide/settings/IDESettingsModel.vue'
import TabsMobile from '@/components/ide/ide/TabsCompMobile.vue'
import { IDEVIEWTABS, IDEVIEWTABS_MOBILE, TAB_ICONS_NAMES } from '@/utils/tabs'
import { ADVANCEDIDETYPE, IDECONSTANT, IDERELETIVEHEIGHT } from '@/utils/ide'
import InputComp from '@/components/ide/ide/InputComp.vue'
import InputFileComp from '@/components/ide/ide/InputFileComp.vue'
import LibraryComp from '@/components/ide/ide/LibraryComp.vue'
import OutputComp from '@/components/ide/ide/OutputComp.vue'
import GenaratedFileComp from '@/components/ide/ide/GenaratedFileComp.vue'
import utilTabsService from '@/services/util.tabs.service'

import tabsService from '@/services/ide/tabs.service'
import advanceExecuteService from '@/services/ide/advanceCompiler.service'
import advanceCompilerDatabaseService from '@/services/ide/advanceCompilerDatabase.service'
import {
  connectConsoleReceivers,
  createConsoleWebSocketService,
  disconnectConsoleWebSocket
} from '@/services/consolews.service'
import { isNextToIDE } from '@/utils/idefn'
import TabsCompRightView from '@/components/ide/ide/TabsCompRightView.vue'
import TabsCompRightButtons from '@/components/ide/ide/TabsCompRightButtons.vue'
import TabCompMaximizeView from '@/components/ide/ide/TabCompMaximizeView.vue'
import { TAB_BUTTON_POSITION } from '@/utils/tabs'
import TabCompBottomButtons from '@/components/ide/ide/TabCompBottomButtons.vue'
import NavigationMenu from '@/components/header/navigation/NavigationMenu.vue'
import Jdroid from '@/components/ide/ide/ai/jdroid/JDroidComp.vue'
import TabsComponent from '@/components/ide/ide/OutputAndGeneratedFilesTabComp.vue'
import GeneratedFileHeader from '@/components/ide/ide/GeneratedFileHeader.vue'
import OutputHeader from '@/components/ide/ide/OutputHeader.vue'
import MobileNavigation from '@/components/header/navigation/MobileNavigation.vue'
import MobileProfileTab from '@/components/header/navigation/MobileProfileTab.vue'
import HTMLOutputComp from '@/components/ide/ide/languages/html/OutputComp.vue'
import DbTerminal from '@/components/ide/ide/DatabaseTerminal.vue'
import ApiTabComp from '@/components/ide/ide/tabs/ApiTabComp/ApiTabComp.vue'
import ProjectTree from '@/components/ide/ide/ProjectTreeComp.vue'
import DataBaseTreeComp from '@/components/ide/ide/DataBaseTreeComp.vue'
import DataBaseInfoComp from '@/components/ide/ide/DataBaseInfoComp.vue'
import DataBaseQueryLog from '@/components/ide/ide/DataBaseQueryLog.vue'
import ChangeLogModal from '@/components/ide/ide/settings/ChangeLogModal.vue'
import AdvanceCompilerInit from '@/components/ide/ide/AdvanceCompilerInit.vue'
import TerminalComp from '@/components/ide/ide/TerminalComp.vue'
import ConsoleComp from '@/components/ide/ide/ConsoleComp.vue'
import ProjectUpdateWarning from '@/components/ide/ide/restrictions/ProjectUpdateWarning.vue'

const props = defineProps({
  shareId: {
    type: String,
    required: false
  },
  collabId: {
    type: String,
    required: false
  },
  genAccessId: {
    type: String,
    required: false
  },
  byPost: {
    type: Boolean,
    required: false,
    default: false
  }
})

const emit = defineEmits(['startCoding'])

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const ideStore = useIdeStore()
const liveCodeStore = useLiveCodeStore()
const navStore = useNavStore()
const advCompilerStore = useAdvanceCompilerStore()
const advCompDbStore = useAdvanceCompilerDatabaseStore()
const tabStore = useTabStore()

const projectManagerStore = useProjectManager()
const jdroidStore = useJdroidStore()
const utilModelsStore = useUtilModelsStore()

let splitBottomRef: ISplitObj = {
  id1: '',
  id2: '',
  data: null
} as ISplitObj
let splitRightRef: ISplitObj = {
  id1: '',
  id2: '',
  data: null
} as ISplitObj

let splitProjectRef: ISplitObj = {
  id1: '',
  id2: '',
  data: null
} as ISplitObj

const splitIdeBar: string = 'splitIdeBar'
const splitTabButtomBar: string = 'splitTabButtomBar'
const splitIde: string = 'splitIde'
const splitIdeTabRight: string = 'splitIdeTabRight'
const splitIdeProjectTree = 'splitIdeProjectTree'
const splitIdeSplitFromProjectTree = 'splitIdeSplitFromProjectTree'
const ideHeightChange = ref(null)
const isCollabOpen = ref<boolean>(false)
const isProjectAutoSaved = ref<boolean>(false)
const isLeavingPage = ref<boolean>(false)

const isProject = computed(() => {
  return ideStore.isProject && ideStore.isProjectId
})

const isSettingPopupHs = computed(() => {
  return ideStore.isSettingPopupHs
})
const isShowLibs = computed(() => {
  return ideStore.ideMeta?.isPlus && ideStore.ideMeta?.library
})
const isUserloggedIn = computed(() => {
  return authStore.isUserloggedIn
})
const isFullScreen = computed(() => {
  return ideStore.isFullScreen
})

const isIdeFullScreen = computed(() => {
  return ideStore.ideFullScreen
})

const langDisplayName = computed(() => {
  return ideStore.ideMeta?.langDisplayName
})
const isCompileLang = computed(() => {
  return ideStore.ideMeta?.isCompile || ideStore.ideMeta?.language === 'java'
})
const isShareNotFound = computed(() => {
  return ideStore.shareNotFound
})
const isShareFoundHttpError = computed(() => {
  return ideStore.shareNotFoundHttpError
})
const isBlockly = computed(() => {
  return ideStore.isBlockly
})

const currentMobileTab = computed(() => {
  return ideStore.currentMobileTab
})

const showJdroid = computed(() => {
  return jdroidStore.showJdroid || ideStore.currentMobileTab !== null
})

const showMaximizeView = computed(() => {
  return tabsService.isAnyTabVisibleOnMaximize()
})

const showRightTabsButtons = computed(() => {
  return tabsService.checkRightTab()
})

const showBottomTabsButtons = computed(() => {
  return tabsService.checkBottomTab()
})

const showBottomTabsView = computed(() => {
  return ideStore.currentMobileTab == null && tabsService.checkBottomTab(null, true)
})

const showRightTabsView = computed(() => {
  return ideStore.currentMobileTab == null && tabsService.checkRightTab(null, true)
})

const showSettingSideBarMobile = computed(() => {
  return ideStore.showSettingSideBarMobile
})

const nonMobileView = computed(() => {
  return ideStore.currentMobileTab == null
})

const isDataBaseRoute = computed(() => {
  return route.meta.advanceCompilerDatabase as boolean
})

const isCheckLogModal = computed(() => {
  return boardingService.checkBoardingStatusAndConditions() ?? false
})

const isAdvanced = computed(() => {
  return ideStore.isAdvanced
})

const projectTreeCollapsed = computed(() => {
  return ideStore.projectTreeCollapsed
})

const isDataBaseView = computed(() => {
  return ideStore.isAdvancedCompilerDatabase
})

const isBoardingActive = computed(() => {
  return ideStore.boardingInstance?.isActivated
})

const checkReleaseWeekend = computed(() => {
  return boardingService.setTimeToShowBoardingTill(currentReleaseDate, 2)
})

const checkChangeLogPopup = computed(() => {
  return (
    isSettingPopupHs.value === IDEVIEWMODELS.CHANGELOG &&
    isCheckLogModal.value &&
    !checkBoardingModal() &&
    checkReleaseWeekend.value
  )
})

const showBoarding = computed(() => {
  return (
    (authStore.isInitiated && ideStore.isIdeView && !isUserloggedIn.value) ||
    (authStore.isInitiated && !isCreateProjectLimitReached.value && isProject.value)
  )
})

const showInitPodView = computed(() => {
  return (
    (route.meta.advanceCompiler &&
      !advCompilerStore.isPodInitializationCompleted &&
      !isLeavingPage.value) ||
    (route.meta.advanceCompilerDatabase &&
      !advCompDbStore.isPodInitializationCompleted &&
      !isLeavingPage.value)
  )
})

const isCreateProjectLimitReached = computed(() => {
  return authStore.isCreateProjectLimitReached
})

const isCreateProjectLimitExceeded = computed(() => {
  return authStore.isCreateProjectLimitExceeded
})

/**
 *
 */
const checkActiveStats = () => {
  if (!isBoardingActive.value) {
    let values = tabStore.activeTabPosition.activeTab
    tabsService.setTabPosition(values, isDataBaseRoute.value, isUserloggedIn.value)
  }
}

/**
 * @description init ide on mount and router change
 */
const initIde = async () => {
  await ideService.initOnRouterChange(route.meta as IMeta)
  isLeavingPage.value = false

  const byPost = route.query.byPost === 'true'
  await ideService.initPostIde(props.byPost || byPost)
  await shareService.initOnRouterChange(
    props.shareId as string,
    props.collabId as string,
    props.genAccessId as string
  )
  await jdroidService.clearChatOnInit()
  await new Promise((resolve) => setTimeout(resolve, 100))
  await projectsService.postProjectLoad()
  advanceCompilerDatabaseService.initFetchQueryLogs()
}

/**
 * @description create collapsable observer
 * @param sizes size
 * @returns boolean
 */
const createCollapsableObserverProjectTree = async (sizes: [number, number]): Promise<boolean> => {
  const [size1] = sizes
  if (size1 < 100) {
    ideStore.setProjectTreeCollapsed(true)
    return true
  } else return false
}

/**
 * Creates an observer to determine if the right tab should collapse based on its size.
 * @param  sizes - An array representing sizes.
 * @param  sizes.0 - The first element of the sizes array, which is not used in this function.
 * @param  sizes.1 - The second element of the sizes array, representing the size to be evaluated (`size2`).
 * @returns A promise that resolves to `true` if `size2` is less than the minimum value required for the current tab, otherwise `false`.
 */
const createCollapsableObserverRightTab = async ([, size2]: [number, number]): Promise<boolean> => {
  if (ideStore.isBlockly) {
    blocklyService.resizeBlockly()
  }
  if (size2 < 250) {
    const tabName = tabsService.checkTabNameCurrentlyVisibleOnRight()
    if (tabName) {
      tabStore.setActiveTabPosition(TAB_BUTTON_POSITION.RIGHT, tabName as TAB_ICONS_NAMES)
    }
    return true
  } else return false
}
/**
 * Creates an observer to determine if the right tab should collapse based on its size.
 * @param  sizes - An array representing sizes.
 * @param  sizes.0 - The first element of the sizes array, which is not used in this function.
 * @param  sizes.1 - The second element of the sizes array, representing the size to be evaluated (`size2`).
 * @returns A promise that resolves to `true` if `size2` is less than the minimum value required for the current tab, otherwise `false`.
 */
const createCollapsableObserverBottomTab = async ([, size2]: [
  number,
  number
]): Promise<boolean> => {
  if (ideStore.isBlockly) {
    blocklyService.resizeBlockly()
  }
  if (ideHeightChange.value) {
    editorService.resizeCodeEditor()
  }
  if (size2 < 1) {
    const tabName = tabsService.checkTabNameCurrentlyVisibleOnBottom()
    if (tabName) {
      tabStore.setActiveTabPosition(TAB_BUTTON_POSITION.BOTTOM, tabName as TAB_ICONS_NAMES)
    }
    return true
  } else return false
}

/**
 * Initialize split view
 * @param splitRef split ref
 * @param id1 element id 1
 * @param id2 element id 2
 * @param size1 size in percentage
 * @param size2 size in percentage
 * @param direction direction
 * @param screenSize breakpoint
 * @param onCollapsable on collapsable callback
 * @param onResize on resize callback
 * @returns null
 */
const initSplit = async (
  splitRef: ISplitObj,
  id1: string,
  id2: string,
  size1: number,
  size2: number,
  direction: ISplitDirection = ISplitDirection.HORIZONTAL,
  screenSize: number = 1024,
  onCollapsable: Function | null = null,
  onResize: Function | null = null
) => {
  await new Promise((resolve) => setTimeout(resolve, 1))
  if (!splitRef) {
    splitRef = {
      id1: '',
      id2: '',
      data: null
    } as ISplitObj
  }
  if (splitRef && splitRef.data === null) {
    return await splitjsService
      .initSplit(id1, id2, size1, size2, direction, screenSize, onCollapsable, onResize)
      .then(async (res) => {
        splitRef = res
        await new Promise((resolve) => setTimeout(resolve, 100))
        editorService.resizeCodeEditor()
        return splitRef as ISplitObj
      })
  }
}

/**
 * Wait for the IDE to be initiated
 * @param count The count
 * @returns boolean
 */
const postNotInit = async (count: number = 0): Promise<boolean> => {
  if (count > 120) {
    return false
  } else {
    if (authStore.isInitiated) {
      return authStore.isUserloggedIn
    } else {
      await new Promise((resolve) => setTimeout(resolve, 100))
      return await postNotInit(count + 1)
    }
  }
}
/**
 * @description post project load
 * @param count number
 * @returns boolean
 */
const postProjectLoad = async (count: number = 0): Promise<boolean> => {
  if (count > 100) return false
  if (!ideStore.project && ideStore.isProjectId) {
    await new Promise((resolve) => setTimeout(resolve, 100))
    return postProjectLoad(count + 1)
  } else {
    return true
  }
}

/**
 * @returns - checking the existing project
 */
const isProjectExistInSession = async () => {
  const existingProjects = sessionStorage.getItem(EXISTING_PROJECT_SESSION.EXISTING)
  const checkAdvance = route?.meta?.isAdvanced || false

  try {
    if (existingProjects && !projectManagerStore.comingFromStartCodingModal) {
      const projectsArray: IProject[] = JSON.parse(existingProjects)

      const matchingProject = projectsArray.find(
        (project) =>
          project.language === route?.meta?.language && project.multipleFile === checkAdvance
      )

      if (matchingProject) {
        await projectsService.listOfCollaborator(matchingProject)
        ideStore.setSelectedProject(null)
        await projectsService.loadProject(matchingProject)
        if (ideStore.isSelectedProject)
          await projectsService.openProject(ideStore.isSelectedProject)
        return true
      }
    }
    return false
  } catch (error) {
    return false
  }
}

/**
 * @description post project load
 * @param count number
 * @returns boolean
 */
const postAceEditorInit = async (count: number = 0): Promise<boolean> => {
  if (count > 100) return false
  if (!ideStore.isWindowAce() || !ideStore.codeEditor) {
    await new Promise((resolve) => setTimeout(resolve, 100))
    return postAceEditorInit(count + 1)
  } else {
    await new Promise((resolve) => setTimeout(resolve, 100))
    return true
  }
}

const showWarningMsg = computed(() => {
  return (
    isCreateProjectLimitExceeded.value ||
    (!ideService.isProjectExist(route?.meta?.isAdvanced, route?.meta?.language) &&
      isCreateProjectLimitReached.value)
  )
})

/**
 * Generate project name and save project
 * @param count number
 * @param initOpen boolean
 * @returns void
 */
const saveProjectHandler = async (
  count: number = 0,
  initOpen: boolean = false
): Promise<boolean | undefined> => {
  const isAdvancedCompilerDatabaseRoute = route.meta.advanceCompilerDatabase
  if (isAdvancedCompilerDatabaseRoute) return
  if (count > 5) {
    isProjectAutoSaved.value = false
    return false
  }
  const checkExistingProject = await isProjectExistInSession()

  /**
   * when saveProjectHandler triggers on directly IDE load or at loggedIn
   */
  if (
    isCreateProjectLimitExceeded.value ||
    (isCreateProjectLimitReached.value && !checkExistingProject)
  )
    return

  if (!checkExistingProject) {
    if (
      isUserloggedIn.value &&
      (!isProjectAutoSaved.value || count > 0) &&
      (!ideStore.isProject || (ideStore.isAdvanced && ideStore.isProject)) &&
      !ideStore.isProjectId
    ) {
      const name = projectManagerStore.generatedProjectName || projectsService.generateProjectName()
      try {
        isProjectAutoSaved.value = true
        if (!projectManagerStore.generatedProjectName) {
          await projectsService.checkForDubplicateProjectName(name)
        }
        if (ideStore.isAdvanced) await postProjectLoad()
        await postAceEditorInit()
        // await projectsService.checkForDubplicateProjectName(name)
        const response = await projectsService.saveProjectActual(true, name)
        if (!ideStore.isAdvanced) ideStore.setProject(response?.data.project)
        if (initOpen && ideStore.isAdvanced && !ideStore.isAdvancedCompiler)
          await ideService.initAdvancedIde(ADVANCEDIDETYPE.OPEN)
        const id = setTimeout(() => {
          isProjectAutoSaved.value = false
          clearTimeout(id)
        }, 1000)
        return true
      } catch (err: any) {
        if (err?.response?.config.url === '/api/doodle/checkFileName') {
          useApiStore().setGlobalMsg('')
        }
        projectManagerStore.setGeneratedProjectName('')
        return await saveProjectHandler(count + 1)
      }
    }
  }
  projectManagerStore.setComingFromStartCodingModal(false)
}

/**
 * startInitIde
 */
const startInitIde = async () => {
  settingService.closePopup()
  ideStore.setIsIdeView(true)
  ideStore.setIsDefaultIde(false)
  ideService.cleanIdeStore()
  ideStore.setRouteMeta(null as any)
  ideStore.setIdeMeta(null as any)
  initIde()

  if (!isDataBaseRoute.value) {
    tabStore.setNonDatabaseTabValues(true, isUserloggedIn.value)
  } else {
    tabStore.setDatabaseTabValues(true, isUserloggedIn.value)
  }
  tabsService.resetMaximizedTabs()

  if (!(route.meta?.isPlus && route.meta?.library)) {
    tabsService.resetExternalLibraryTabs()
  }
}

/**
 * checking the boarding modal
 * @returns - boolean
 */
const checkBoardingModal = () => {
  return boardingService.getBoardingSteps()?.boardingModal
}

/**
 * check the condition and initialize the boarding
 */
const openBoarding = () => {
  if (
    checkBoardingModal() &&
    isCheckLogModal.value &&
    !isBoardingActive.value &&
    checkReleaseWeekend.value
  )
    boardingService.initializedBoardingSteps()
  else settingService.openPopup(IDEVIEWMODELS.CHANGELOG)
}

/**
 * @description initialize right split js
 */
const initRightSplit = () => {
  initSplit(
    splitRightRef,
    splitIdeSplitFromProjectTree,
    splitIdeTabRight,
    60,
    40,
    ISplitDirection.HORIZONTAL,
    640,
    createCollapsableObserverRightTab
  ).then((res) => {
    splitRightRef = res as ISplitObj
  })
}
/**
 * @description initialize bottom split js
 */
const initBottomSplit = () => {
  initSplit(
    splitBottomRef,
    splitIdeBar,
    splitTabButtomBar,
    60,
    40,
    ISplitDirection.VERTICAL,
    640,
    createCollapsableObserverBottomTab
  ).then((res) => {
    splitBottomRef = res as ISplitObj
  })
}

onBeforeMount(async () => {
  watchEffect(async () => {
    const isAdvancedCompilerRoute = route.meta.advanceCompiler
    const isGeneralAccessId = !!props.genAccessId
    const isAdvancedCompilerDatabaseRoute = route.meta.advanceCompilerDatabase

    if (isAdvancedCompilerRoute || isGeneralAccessId || isAdvancedCompilerDatabaseRoute) {
      await postNotInit()

      if (!authStore.isUserloggedIn) {
        router.push({ name: 'login-to-continue' })
      }
    }
  })

  startInitIde()

  // make ideMeta reactive
  watch(route, async () => {
    if (route.meta.canonicalPath) {
      await jdroidService.clearChat()
      initIde()
      const isAdvancedCompilerDatabaseRoute = route.meta.advanceCompilerDatabase
      if (isUserloggedIn.value && !isAdvancedCompilerDatabaseRoute)
        await saveProjectHandler(0, true)
    }
  })

  watch(
    () => ideStore.project,
    async () => {
      await new Promise((resolve) => setTimeout(resolve, 600))
      editorService.setCodeEditorReadOnly()
    }
  )
})

onMounted(async () => {
  utilModelsService.onCloseModel(async (hs: string | null) => {
    await new Promise((resolve) => setTimeout(resolve, 100))
    const currentModal = Object.values(AUTHMODELHS).find((values) =>
      document.getElementById(values)?.getAttribute('aria-overlay')
    )
    // if hs on AUTHMODELHS.STARTCODING, then start coding
    if (
      hs &&
      Object.values(AUTHMODELHS).includes(hs) &&
      isEmpty(currentModal) &&
      ideStore.showStartCodingModal
    )
      emit('startCoding')
  })

  //Mobile flags
  if (window.innerWidth < 640) {
    ideStore.setFullScreen(false)
    ideStore.setMobileTab(IDEVIEWTABS_MOBILE.IDE) //default tab
  }

  if (window.innerHeight < 690) {
    ideStore.setMobileHeightLessThan690(true)
  }

  window.addEventListener('resize', async () => {
    editorService.resizeCodeEditor()
    blocklyService.resizeBlockly()

    if (window.innerWidth < 1024) {
      ideStore.setFullScreen(false)
    }

    //When enlarging to desktop, reset store to show desktop IDE
    if (window.innerWidth >= 640 && currentMobileTab.value != null) {
      ideStore.setMobileTab(null)
    }

    //When resizing from desktop to mobile, set default tab to IDE
    if (window.innerWidth < 640 && currentMobileTab.value == null) {
      ideStore.ideFullScreen = false
      ideStore.setMobileTab(IDEVIEWTABS_MOBILE.IDE)
    }

    //Mobile height check
    if (window.innerHeight < 690) {
      ideStore.setMobileHeightLessThan690(true)
    } else {
      ideStore.setMobileHeightLessThan690(false)
    }
  })

  // Watch the change of mode in other tabs/windows
  window.addEventListener('storage', function (e) {
    if (e.key === 'jdoodle-mode' && ideStore.isIdeView) {
      ideService.setModeClass(e.newValue, true, false)
    }
  })

  watch(
    () => props.collabId,
    () => {
      const id = props.collabId as string
      useLiveCodeStore().setCollabId(id)
    }
  )

  window.onbeforeunload = function () {
    if (ideStore.isCodeUpdated) {
      projectsService.autoSave()
      return `Are you sure you want to move from this ${langDisplayName.value}${
        isCompileLang.value ? ' Compiler' : ''
      } IDE?`
    }
  }
  ideService.setMode()
  historyService.checkStorage()
  await ideService.initIde()
  projectsService.initOnRouterChange()

  if (props.collabId && !isUserloggedIn.value) {
    isCollabOpen.value = true
    utilModelsService.openModal(AUTHMODELHS.LOGIN)
  }
  // GTM
  gtmService.sentEvent({ event: 'visits_ide' })
})

watch(
  () => ideStore.project?.id,
  async () => {
    if (ideStore.isAdvancedCompiler) {
      await advanceExecuteService.cleanAdvanceCompilerStore()
      if (ideStore.project?.id) await ideService.initAdvancedIde(ADVANCEDIDETYPE.OPEN)
      await advanceExecuteService.initAdvanceCompiler()
    }
  }
)

watch(
  () => [
    showBoarding.value,
    advCompilerStore.isPodInitializationCompleted,
    advCompDbStore.isPodInitializationCompleted,
    authStore.onboarded,
    ideStore.boardingAfterLogin
  ],
  () => {
    if (!isUserloggedIn.value || authStore.onboarded || ideStore.boardingAfterLogin) {
      if (
        showBoarding.value &&
        !ideStore.isAdvancedCompiler &&
        !ideStore.isAdvancedCompilerDatabase
      ) {
        openBoarding()
      } else if (
        advCompilerStore.isPodInitializationCompleted ||
        advCompDbStore.isPodInitializationCompleted
      ) {
        openBoarding()
      }
      if (ideStore.boardingAfterLogin) ideStore.setBoardingAfterLogin(false)
    }
  }
)

watch(
  () => isBoardingActive.value,
  (val) => {
    if (val) {
      ideStore.setBoardingResetTab(true)
    }
    if (ideStore.boardingResetTab && !val) {
      if (!isDataBaseRoute.value) {
        tabStore.setNonDatabaseTabValues(true, isUserloggedIn.value)
      } else {
        tabStore.setDatabaseTabValues(true, isUserloggedIn.value)
      }
      ideStore.setBoardingResetTab(false)
    }
  }
)

watch(
  () => [ideStore.isAdvancedCompilerDatabase, ideStore.isLanguage],
  async () => {
    if (ideStore.isAdvancedCompilerDatabase) {
      await advanceCompilerDatabaseService.cleanAdvanceCompilerDatabaseStore()
      await advanceCompilerDatabaseService.initAdvanceCompilerDatabase()
    }
  },
  { immediate: true }
)

onBeforeRouteLeave((to, from, next) => {
  isLeavingPage.value = true
  ideStore.setFullScreen(false)
  ideStore.setHasErrorAfterExecute(false)
  if (route.meta.advanceCompiler) {
    disconnectConsoleWebSocket()
  }
  advanceExecuteService.cleanAdvanceCompilerStore()
  advanceCompilerDatabaseService.cleanAdvanceCompilerDatabaseStore()
  if (window.innerWidth > 640) {
    ideStore.setMobileTab(null)
    utilTabsService.setActiveTab(IDEVIEWTABS.OUTPUT)
  } else {
    ideStore.setMobileTab(IDEVIEWTABS_MOBILE.IDE)
  }
  if (ideStore.isCodeUpdated && projectManagerStore.projectPermissionWithId?.readOnly !== true) {
    if (!isUserloggedIn.value) {
      const isNextToIDE_ = isNextToIDE(to.path)
      let confirmModal: Object = {}
      if (isNextToIDE_) {
        confirmModal = {
          title: 'Hold on! Project in progress...',
          subtitle: 'Your unsaved changes will be lost.',
          retunHs: null,
          yesBtnText: 'Start Fresh',
          noBtnText: 'Go Back'
        }
      } else {
        confirmModal = {
          title: `Are you sure you want to move from this ${langDisplayName.value}${
            isCompileLang.value ? ' Compiler' : ''
          } IDE?`,
          subtitle: 'You will lose all your unsaved changes.'
        }
      }
      utilModelsService
        .confirmPromise(...Object.values(confirmModal))
        .then(async () => {
          await utilModelsService.closeModalAsync(CONFIRM.CONFIRM)
          settingService.closePopup()
          jdroidService.clearChat()
          ideStore.setCodeUpdated(false) // Temp fix
          next()
        })
        .catch(() => {
          next(false)
        })
    } else {
      const infoModekl: Object = {
        title: 'Saving your changes.',
        subtitle: 'JDoodle is saving your changes. Please wait...'
      }
      if (liveCodeStore.isLiveCodingActive) liveCodingService.disconnectLiveCoding()
      utilModelsService.showInfo(...Object.values(infoModekl))
      projectsService.autoSave().finally(() => {
        utilModelsService.hideInfo()
        next()
      })
    }
  } else {
    if (liveCodeStore.isLiveCodingActive) liveCodingService.disconnectLiveCoding()
    next()
  }
  projectManagerStore.setComingFromProjectManager(false)
  ideStore.setCollabId(null)
  ideStore.setGenAccessId(null)

  if (ideStore.isAdvancedCompilerDatabase) {
    ideStore.setCleanUniqueActiveItemAdvIde()
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', () => {})
  window.removeEventListener('storage', () => {})

  splitjsService.ejectSplitObjArr()

  settingService.closePopup()
  ideStore.setIsIdeView(false)
  ideService.cleanIde()
  ideService.cleanIdeStore()
  if (route.meta.advanceCompiler) {
    disconnectConsoleWebSocket()
  }
  advanceExecuteService.cleanAdvanceCompilerStore()
  advanceCompilerDatabaseService.cleanAdvanceCompilerDatabaseStore()
  jdroidService.clearChat()
  jdroidStore.setShowJdroid(false) // Display Jdroid
  jdroidStore.setShowNotification(false) // Close notification
  ideService.removeDarkClass()
  projectManagerStore.setGeneratedProjectName('')
})

watch(currentMobileTab, async () => {
  if (currentMobileTab.value == IDEVIEWTABS_MOBILE.IDE) {
    await new Promise((resolve) => setTimeout(resolve, 10))
    editorService.resizeCodeEditor()
    blocklyService.resizeBlockly()
  } else if (currentMobileTab.value == IDEVIEWTABS_MOBILE.OUTPUT) {
    await new Promise((resolve) => setTimeout(resolve, 100))
    editorService.resizeOutputEditor()
  }
})
watch(
  isUserloggedIn,
  async (val) => {
    if (val) {
      await ideService.initIde()
      if (props.collabId && isUserloggedIn.value && isCollabOpen.value) {
        initIde()
      }
      isCollabOpen.value = false
      tabsService.getTabPositions()

      if (!isDataBaseRoute.value) {
        tabStore.setNonDatabaseTabValues(false, isUserloggedIn.value)
      } else {
        tabStore.setDatabaseTabValues(false, isUserloggedIn.value)
      }
      if (!projectManagerStore.comingFromProjectManager) {
        if (!(props.shareId || props.genAccessId || props.collabId)) {
          if (projectManagerStore.generatedProjectName && isUserloggedIn.value)
            await projectsService
              .checkForDubplicateProjectName(projectManagerStore.generatedProjectName)
              .catch(() => {
                useApiStore().setGlobalMsg('')
                projectManagerStore.setGeneratedProjectName('')
              })
          if (!props.genAccessId) {
            await saveProjectHandler(0, true)
          }
        }
      }
    }
  },
  { immediate: true }
)

watch(
  () => ideStore.project,
  () => {
    if (ideStore.genAccessId && !liveCodeStore.isLiveCodingActive && ideStore.project) {
      if (!liveCodeStore.isLiveCodingActive) liveCodingService.initColab()
      liveCodeStore.setIsProjectLoaded(true)
      useProjectManager().setProjectPermissionWithId({
        ...useProjectManager().projectPermissionWithId,
        readOnly: ideStore.genAccessPermission === 'READ_ONLY' ? true : false
      })
      editorService.setCodeEditorReadOnly()
    }
    if (ideStore.isCollabId && !liveCodeStore.isLiveCodingActive && ideStore.project) {
      liveCodingService.initColab()
      liveCodeStore.setIsProjectLoaded(true)
    }
  }
)

watch(
  () => ideStore.colorMode,
  (val) => {
    ideService.setModeClass(val, false, false)
  }
)

watch(
  showBottomTabsView,
  async () => {
    if (showBottomTabsView.value) {
      initBottomSplit()
      if (ideStore.isBlockly) {
        blocklyService.resizeBlockly()
      }
    } else {
      splitjsService.destroySplit(splitBottomRef, true)
      await new Promise((resolve) => setTimeout(resolve, 100))
      editorService.resizeCodeEditor()
      if (ideStore.isBlockly) {
        blocklyService.resizeBlockly()
      }
    }
  },
  { immediate: true }
)

watch(
  () => ideStore.ideFullScreen,
  async (val) => {
    if (val) {
      splitjsService.destroySplit(splitRightRef, true)
      splitjsService.destroySplit(splitBottomRef, true)
      await new Promise((resolve) => setTimeout(resolve, 100))
      editorService.resizeCodeEditor()
    } else {
      if (showBottomTabsView.value) {
        initBottomSplit()
      }
      if (showRightTabsView.value) {
        initRightSplit()
      }
    }
    if (ideStore.isBlockly) {
      blocklyService.resizeBlockly()
    }
  }
)

watch(
  showRightTabsView,
  async () => {
    if (showRightTabsView.value) {
      initRightSplit()
      if (ideStore.isBlockly) {
        blocklyService.resizeBlockly()
      }
    } else {
      splitjsService.destroySplit(splitRightRef, true)
      await new Promise((resolve) => setTimeout(resolve, 100))
      editorService.resizeCodeEditor()
      if (ideStore.isBlockly) {
        blocklyService.resizeBlockly()
      }
    }
  },
  { immediate: true }
)

watch(
  () => [projectTreeCollapsed.value, ideStore.isAdvanced, ideStore.currentMobileTab],
  async (value) => {
    const projectTreeCollapsedValue = value[0]
    const ideStoreAdvancedValue = value[1]
    if (!projectTreeCollapsedValue && ideStoreAdvancedValue && !ideStore.currentMobileTab) {
      initSplit(
        splitProjectRef,
        splitIdeProjectTree,
        splitIdeSplitFromProjectTree,
        20,
        80,
        ISplitDirection.HORIZONTAL,
        640,
        createCollapsableObserverProjectTree
      ).then((res) => {
        splitProjectRef = res as ISplitObj
      })
    } else {
      splitjsService.destroySplit(splitProjectRef, true)
      await new Promise((resolve) => setTimeout(resolve, 100))
      editorService.resizeCodeEditor()
    }
  },
  { immediate: true }
)

watch(
  () => [tabStore.activeTabPosition],
  () => {
    checkActiveStats()
  },
  { deep: true }
)

watch(isShowLibs, () => {
  if (!isShowLibs.value) {
    tabsService.updateTabVisiblity(TAB_ICONS_NAMES.EXTERNAL_LIBRARIES)
    tabsService.resetExternalLibraryTabs()
  }
})

watch(isDataBaseRoute, (value) => {
  if (!value) {
    tabStore.setNonDatabaseTabValues(false, isUserloggedIn.value)
  } else {
    tabStore.setDatabaseTabValues(false, isUserloggedIn.value)
  }
})
watch(
  () => [
    projectManagerStore.generatedProjectName,
    utilModelsStore.showInfo,
    utilModelsStore.isConfirmPromiseFinish,
    projectManagerStore.deleteProjectInProgress
  ],
  async ([generatedProjectName, isSavingModalOpen, isAlertModalClose, deleteProjectInProgress]) => {
    if (
      !generatedProjectName ||
      isSavingModalOpen ||
      !isAlertModalClose ||
      isSettingPopupHs.value === IDEVIEWMODELS.PROJECTSHARE ||
      deleteProjectInProgress
    )
      return
    if (!ideStore.isAdvanced) ideStore.setProject(null)
    // Waiting for router to update
    await new Promise((resolve) => setTimeout(resolve, 100))
    await saveProjectHandler(0, true)
  }
)
watch(
  () => liveCodeStore.isLiveCodingActive,
  async () => {
    if (ideStore.genAccessId && liveCodeStore.isLiveCodingActive) {
      const liveCodeAction: ILiveCodeActions = {
        type: LiveCodeActionsType.CALL_COLLAB,
        data: {}
      }
      await liveCodingService.broudcastMessage(liveCodeAction)
    }
  }
)

watch(
  () => [advCompilerStore.isPodInitializationCompleted, ideStore.project?.id],
  ([init]) => {
    if (ideStore.isAdvancedCompiler) {
      projectManagerStore.setProjectPermissionWithId({
        ...projectManagerStore.projectPermissionWithId,
        readOnly: !init ? true : false
      })
      editorService.setCodeEditorReadOnly()
    } else if (ideStore.isAdvancedCompilerDatabase) {
      projectManagerStore.setProjectPermissionWithId({
        ...projectManagerStore.projectPermissionWithId,
        readOnly: false
      })
    }
  }
)

watch(isShareNotFound, (notFound) => {
  if (notFound && props.genAccessId && isUserloggedIn.value) {
    useApiStore().setGlobalMsg('')
    setTimeout(() => router.push({ name: 'organisation-home' }), 3000)
  }
})

watch(
  () => [
    route.meta.advanceCompiler,
    advCompilerStore.projectIdAdvCompiler,
    advCompilerStore.isPodInitializationCompleted
  ],
  ([val1, val2, val3]) => {
    if (val1 && val2 && val3 && advCompilerStore.console.socket == null) {
      const socket = createConsoleWebSocketService()
      advCompilerStore.setConsoleSocketInstance(socket)
      connectConsoleReceivers()
    }
  },
  {
    immediate: true
  }
)

watch(isCreateProjectLimitReached, (value) => {
  if (!value) {
    if (isUserloggedIn.value && !ideStore.isAdvancedCompilerDatabase && !props.genAccessId) {
      saveProjectHandler()
    }
  }
})

watch(
  showWarningMsg,
  (value) => {
    useProjectManager().setProjectPermissionWithId({
      ...useProjectManager().projectPermissionWithId,
      readOnly: value
    })
    editorService.setCodeEditorReadOnly()
    editorService.resizeCodeEditor()
  },
  { immediate: true }
)
</script>

<template>
  <NotLoginModel />
  <Recaptcha />

  <!-- Screen -->
  <div class="section-new-white relative flex h-full flex-col">
    <PrintBlocker />
    <p
      v-if="isShareNotFound"
      :class="[
        'print:hidden, p-small ',
        'error',
        'text-center',
        'my-5',
        { hidden: !isShareNotFound }
      ]"
    >
      {{ isShareFoundHttpError || 'Share not found' }}
    </p>
    <div class="flex flex-col">
      <!-- IDE and Tabs -->
      <div
        v-show="!isShareNotFound"
        class="section-new-quinary flex h-screen flex-col"
        :id="IDECONSTANT.IDEHOMEVIEW"
        :class="[
          'flex flex-col sm:overflow-clip',
          {
            'h-full w-full': isFullScreen
          },
          {
            'h-full w-full': !isFullScreen
          }
        ]"
      >
        <!-- Start IDE Header -->
        <div
          class="section-new-white block h-fit max-h-[60px] min-h-[60px] w-full overflow-visible sm:hidden sm:max-h-full"
        >
          <div class="flex h-full w-full flex-col items-center sm:items-start">
            <div class="xs:p-0 flex h-full w-full items-center justify-between sm:px-3 sm:py-3">
              <NavigationMenu class="block h-full w-full sm:hidden" :isMobileNav="true" />
            </div>
          </div>
        </div>
        <!-- END IDE Header -->

        <div
          :class="[
            'flex h-full w-full overflow-x-hidden  overflow-y-hidden',
            {
              'flex-col': isFullScreen
            },
            {
              'flex-col': !isFullScreen
            }
          ]"
        >
          <NewProject v-show="isSettingPopupHs === IDEVIEWMODELS.NEWPROJECT" />
          <MyProjectModel
            ref="myProjectModelRef"
            v-if="isSettingPopupHs === IDEVIEWMODELS.MYPROJECTS"
          />
          <ExecurteHistoryModel
            ref="execurteHistoryModelRef"
            v-show="isSettingPopupHs === IDEVIEWMODELS.EXECUTEHISTORY"
          />
          <SaveModel v-show="isSettingPopupHs === IDEVIEWMODELS.SAVE" />
          <SaveAsModel v-show="isSettingPopupHs === IDEVIEWMODELS.SAVEAS" />
          <EditableShareModel
            v-if="isProject"
            v-show="isSettingPopupHs === IDEVIEWMODELS.EDITABLESHARE"
          />
          <ProjectShareModal v-show="isSettingPopupHs === IDEVIEWMODELS.PROJECTSHARE" />
          <IDESettingsModel v-show="isSettingPopupHs === IDEVIEWMODELS.IDESETTINGS" />
          <ChangeLogModal v-if="checkChangeLogPopup" />
          <div class="relative flex h-full w-full flex-row">
            <div
              v-if="navStore.showMobileNav && ideStore.currentMobileTab != null"
              class="absolute z-50 h-full"
            >
              <MobileNavigation />
            </div>
            <div
              v-if="navStore.showMobileProfileNav && ideStore.currentMobileTab != null"
              class="absolute right-0 z-50 h-full"
            >
              <MobileProfileTab />
            </div>
            <div
              v-if="showSettingSideBarMobile || nonMobileView"
              :class="[
                'z-10',
                {
                  'w-max': !ideStore.isSideBarCollapsed,
                  'w-[40px] ': ideStore.isSideBarCollapsed,
                  hidden: isIdeFullScreen || showMaximizeView
                }
              ]"
              class="justify-center transition-all print:hidden"
            >
              <div
                :class="[
                  'stroke-primary-default background-quaternary h-full flex-row border-r',
                  { 'w-auto': ideStore.isSideBarCollapsed }
                ]"
              >
                <SettingsSideBar />
              </div>
            </div>

            <div class="flex h-full w-full flex-col">
              <ProjectUpdateWarning v-if="showWarningMsg" />
              <div
                ref="ideHeightChange"
                :class="[
                  'section-new-quaternary max-h-auto flex h-full  w-full justify-between ',
                  {
                    'min-w-full ': isFullScreen
                  },

                  {
                    'min-w-full grow sm:grow-0': !isFullScreen
                  }
                ]"
              >
                <div
                  :class="[
                    ,
                    {
                      'section-primary flex h-auto flex-row': showJdroid
                    },
                    {
                      'w-full':
                        ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.IDE ||
                        ideStore.currentMobileTab == null ||
                        ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.JDROID
                    },

                    {
                      'fixed top-0 z-50 h-screen w-screen': isIdeFullScreen
                    }
                  ]"
                >
                  <div class="flex h-full w-full flex-row">
                    <div :id="splitIdeProjectTree">
                      <div
                        v-if="
                          isAdvanced &&
                          !projectTreeCollapsed &&
                          !isDataBaseView &&
                          (ideStore.currentMobileTab == null ||
                            ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.IDE)
                        "
                        class="surface-secondary-base h-full w-full"
                        :class="ideStore.currentMobileTab != null ? 'min-w-[225px]' : ''"
                      >
                        <ProjectTree />
                      </div>
                      <div
                        v-if="
                          isAdvanced &&
                          !projectTreeCollapsed &&
                          isDataBaseView &&
                          (ideStore.currentMobileTab == null ||
                            ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.IDE)
                        "
                        class="flex h-full flex-col justify-between rounded-md"
                        :class="ideStore.currentMobileTab != null ? 'min-w-[225px]' : ''"
                      >
                        <div class="surface-secondary-base h-2/5 overflow-hidden">
                          <DataBaseTreeComp />
                        </div>

                        <div class="h-2/5">
                          <DataBaseQueryLog />
                        </div>

                        <div
                          v-if="isAdvanced && !projectTreeCollapsed && isDataBaseView"
                          class="surface-secondary-base stroke-primary-default h-1/5 border-r border-t"
                          :id="BoardingElement.IDE_DATABASE_INFO"
                        >
                          <DataBaseInfoComp />
                        </div>
                      </div>
                    </div>

                    <div :id="splitIdeSplitFromProjectTree" class="flex h-full w-full flex-col">
                      <div :id="splitIdeBar" class="flex h-full w-full flex-row">
                        <Ide
                          :id="splitIde"
                          :class="[
                            'h-[100%]',
                            {
                              hidden: !(
                                ideStore.currentMobileTab == null ||
                                ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.IDE
                              )
                            }
                          ]"
                        />
                      </div>
                      <div
                        :id="splitTabButtomBar"
                        v-if="!isIdeFullScreen && showBottomTabsButtons && currentMobileTab == null"
                        class="surface-secondary-base z-20 flex w-full flex-col overflow-x-auto"
                      >
                        <div
                          v-if="!isIdeFullScreen && showBottomTabsView"
                          class="background-quaternary hidden w-full min-w-64 items-center justify-center overflow-x-auto overflow-y-hidden sm:flex"
                        >
                          <TabCompBottomButtons />
                        </div>
                        <div
                          :class="[
                            'background-primary hidden h-full w-full flex-col overflow-auto sm:flex',
                            {
                              'border-primary min-w-full overflow-auto': isFullScreen
                            },
                            {
                              'max-h-[60px] min-w-64 overflow-hidden sm:max-h-full sm:overflow-auto':
                                !isFullScreen
                            },
                            {
                              hidden: !showBottomTabsView,
                              visible: showBottomTabsView && currentMobileTab == null
                            }
                          ]"
                        >
                          <!-- v-if condition solves output bug (due to duplicate ID in component) -->
                          <Tabs class="hidden h-full overflow-hidden sm:block" />
                        </div>
                      </div>
                      <div
                        v-if="!isIdeFullScreen && showBottomTabsButtons && !showBottomTabsView"
                        class="hidden w-full items-center justify-center sm:flex"
                      >
                        <TabCompBottomButtons />
                      </div>
                    </div>

                    <div
                      v-if="showRightTabsView && !isIdeFullScreen && currentMobileTab == null"
                      class="flex h-full w-full overflow-x-auto"
                      :id="splitIdeTabRight"
                    >
                      <TabsCompRightView class="hidden h-full w-full sm:flex" />
                    </div>
                    <div
                      v-if="!isIdeFullScreen && showRightTabsButtons && !showRightTabsView"
                      class="ml-auto hidden h-full sm:flex"
                    >
                      <TabsCompRightButtons />
                    </div>
                  </div>
                </div>

                <!-- Maximize tabs components -->
                <div
                  v-if="showMaximizeView && currentMobileTab == null"
                  class="fixed top-0 z-50 h-screen w-screen"
                >
                  <TabCompMaximizeView />
                </div>

                <!-- START Mobile tabs components -->
                <Jdroid
                  class="h-[100%]"
                  v-if="!isBlockly && ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.JDROID"
                  :currentPosition="TAB_BUTTON_POSITION.BOTTOM"
                  :class="[
                    {
                      hidden: !(
                        ideStore.currentMobileTab == null ||
                        ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.JDROID
                      )
                    }
                  ]"
                />

                <InputComp
                  :class="[{ hidden: !(ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.INPUT) }]"
                />

                <InputFileComp
                  :class="[
                    { hidden: !(ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.INPUTFILES) }
                  ]"
                />

                <LibraryComp
                  v-if="isShowLibs"
                  :class="[
                    { hidden: !(ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.LIBRARIES) }
                  ]"
                />
                <HTMLOutputComp v-if="ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.OUTPUTHTML" />

                <DbTerminal v-if="ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.DB_TERMINAL" />
                <ApiTabComp v-if="ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.APITAB" />
                <TerminalComp v-if="ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.TERMINAL" />
                <ConsoleComp v-if="ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.CONSOLE" />

                <div
                  v-if="currentMobileTab !== null"
                  class="section-primary flex w-full flex-col gap-4 overflow-auto md:overflow-hidden"
                  :class="[
                    { hidden: !(ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.OUTPUT) },
                    {
                      'surface-secondary-base px-4 sm:p-2':
                        ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.OUTPUT
                    }
                  ]"
                >
                  <TabsComponent :tabs="[{ label: OutputHeader }, { label: GeneratedFileHeader }]">
                    <template #slot1>
                      <OutputComp />
                    </template>
                    <template #slot2>
                      <GenaratedFileComp />
                    </template>
                  </TabsComponent>
                </div>
                <div
                  v-if="ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.SOCIALSHARE"
                  class="h-full w-full overflow-auto"
                >
                  <FooterProduct
                    :class="[
                      { hidden: !(ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.SOCIALSHARE) }
                    ]"
                  />
                </div>

                <!-- END Mobile tabs components -->
              </div>
              <!-- END Ide and Settings Sidebar -->
            </div>
          </div>
          <div v-if="showInitPodView" class="absolute z-40 flex h-full w-full bg-white">
            <AdvanceCompilerInit :isDataBaseRoute="isDataBaseRoute" />
          </div>
        </div>
      </div>
      <TabsMobile
        :id="IDERELETIVEHEIGHT.EXCLUDEID"
        class="section-quinary border-primary border-b border-t sm:border-none"
      />
      <div :id="IDERELETIVEHEIGHT.EXCLUDEID" class="hidden sm:block">
        <!-- Share/Feedback -->
        <div class="flex h-fit w-full">
          <FooterProduct />
        </div>
      </div>
    </div>
  </div>
  <!-- END Screen --->
</template>

<style>
.gutter {
  background-repeat: no-repeat;
  background-position: 50%;
  z-index: 1;
}

.gutter.gutter-horizontal {
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAeCAYAAADkftS9AAAAIklEQVQoU2M4c+bMfxAGAgYYmwGrIIiDjrELjpo5aiZeMwF+yNnOs5KSvgAAAABJRU5ErkJggg==');
  float: left;
  cursor: ew-resize;
  position: relative;
  background-color: theme('colors.white/50');
}

.gutter.gutter-horizontal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 100%;
  background-color: transparent;
  transition: background-color 0.3s ease;
}

.gutter.gutter-horizontal:hover::before {
  background-color: theme('colors.blue.500');
}

.gutter.gutter-vertical {
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAFAQMAAABo7865AAAABlBMVEVHcEzMzMzyAv2sAAAAAXRSTlMAQObYZgAAABBJREFUeF5jOAMEEAIEEFwAn3kMwcB6I2AAAAAASUVORK5CYII=');
  cursor: ns-resize;
  background-color: theme('colors.white/50');
  position: relative;
}

.gutter.gutter-vertical::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 4px;
  width: 100%;
  background-color: transparent;
  transition: background-color 0.3s ease;
}

.gutter.gutter-vertical:hover::before {
  background-color: theme('colors.blue.500');
}

/* Dark mode styles */
#app.dark .gutter.gutter-vertical {
  background-color: theme('colors.new-neutral.900/50');
}

#app.dark .gutter.gutter-horizontal {
  background-color: theme('colors.new-neutral.900/50');
}

#status {
  position: fixed;
  background: #bbb;
  width: 100px;
  border: 1px solid #888;
  right: 10px;
  top: 10px;
  z-index: 10;
  color: #000;
  font-family: sans-serif;
  font-size: 10pt;
  padding: 5px;
}

#users {
  border-top: 1px solid #888;
  margin-top: 5px;
}

#editor {
  border: 1px solid #000;
  width: 100vw;
  height: 100vh;
}

.cursor-label {
  color: #fff;
  padding: 2px;
  font-size: 8pt;
  position: absolute;
  left: -2px;
}
</style>
